import React, { useState, useMemo } from "react"
import MyCard from "@common-utils/MyCard"
import Step3ManageCandidate from "./Step3ManageCandidate"
import { ICreateCandidatResponseModel } from "../../core/_models"
import FileAndDriveHelper from "@common-helpers/FileAndDriveHelper"
import MyModal from "@common-utils/MyModal"
import { useAuth } from "../../../../../../AppContext"
import { addCandidatesToListeTraitement, removeCandidatesFromListeTraitement } from "../../../core/_requests"
import clsx from "clsx"
import { ICreateCandidatStep3Model } from "../../core/_models" // Import the new model

interface Props {
     items: ICreateCandidatStep3Model[] // Use extended model here
     onAddAnother: () => void
     onUpdateItems: (items: ICreateCandidatStep3Model[]) => void // Use extended model here
}

const Step3ManagementView = ({ items, onAddAnother, onUpdateItems }: Props) => {
     const [previewItem, setPreviewItem] = useState<ICreateCandidatResponseModel | null>(null)
     const { setCurrentUser } = useAuth()

     // Check if all items are already in the processing list to toggle the global button state
     const areAllInProcessingList = useMemo(() => {
          return items.every(item => item.localIsInProcessingList)
     }, [items])

     const handleUpdateCandidateState = (index: number, updatedFields: any) => {
          const newItems = [...items]
          newItems[index] = { ...newItems[index], ...updatedFields }
          onUpdateItems(newItems)
     }

     const updateUserCounter = (affected: number) => {
          setCurrentUser(prev => {
               if (prev) {
                    return {
                         ...prev,
                         nbCandidatsDansListeTraitement: (prev.nbCandidatsDansListeTraitement || 0) + affected,
                    }
               }
               return undefined
          })
     }

     const handleToggleAllProcessingList = () => {
          const ids = items.map(i => i.candidat.id!)

          if (!areAllInProcessingList) {
               // Add all that aren't already there
               const candidatesToProcess = items.filter(i => !i.localIsInProcessingList)
               const idsToProcess = candidatesToProcess.map(i => i.candidat.id!)

               addCandidatesToListeTraitement(idsToProcess).then(r => {
                    const newItems = items.map(item => ({
                         ...item,
                         localIsInProcessingList: true,
                    }))
                    onUpdateItems(newItems)
                    updateUserCounter(r.data.affectedCandidates)
               })
          } else {
               // Remove all
               removeCandidatesFromListeTraitement(ids).then(r => {
                    const newItems = items.map(item => ({
                         ...item,
                         localIsInProcessingList: false,
                    }))
                    onUpdateItems(newItems)
                    updateUserCounter(-r.data.affectedCandidates)
               })
          }
     }

     return (
          <div className="d-flex flex-column gap-7 animate__animated animate__fadeIn bg-body p-6 rounded-4 border-2 border-dashed border-gray-300">
               {/* Header */}
               <div className="d-flex flex-column flex-sm-row flex-stack flex-wrap gap-4 px-2">
                    <div className="d-flex align-items-center">
                         <div className="bg-success bg-opacity-10 p-3 rounded-circle me-4 border border-success border-opacity-20 shadow-sm">
                              <i className="fas fa-check-double text-success fs-3"></i>
                         </div>
                         <div className="d-flex flex-column">
                              <h2 className="fw-boldest text-gray-900 fs-1 mb-0 lh-1">Importation terminée</h2>
                              <div className="text-muted fw-bold fs-7 mt-1">
                                   Session active : <span className="text-primary fw-boldest">{items.length}</span> profils synchronisés.
                              </div>
                         </div>
                    </div>
                    <div className="d-flex flex-column flex-sm-row gap-3 w-100 w-sm-auto">
                         {/* Bulk Action Button */}
                         <button
                              className={clsx("btn btn-sm shadow-sm px-8 fw-boldest h-40px rounded-3 w-100 w-sm-auto", areAllInProcessingList ? "btn-info" : "btn-light-info")}
                              onClick={handleToggleAllProcessingList}
                         >
                              <i className={clsx("fas fs-7 me-2", areAllInProcessingList ? "fa-check-circle" : "fa-inbox")}></i>
                              {areAllInProcessingList ? "TOUS DANS LA LISTE" : "TOUT METTRE À TRAITER"}
                         </button>

                         <button className="btn btn-sm btn-primary shadow-sm px-8 fw-boldest h-40px rounded-3 w-100 w-sm-auto" onClick={onAddAnother}>
                              <i className="fas fa-redo-alt fs-7 me-2"></i>NOUVEL AJOUT
                         </button>
                    </div>
               </div>

               {/* Grille des candidats */}
               <div className="row g-5 row-cols-1 row-cols-lg-2 row-cols-xxl-3">
                    {items.map((item, index) => (
                         <div className="col" key={item.candidat.id}>
                              <MyCard classNames="h-100 border-0 shadow-sm transition-all bg-body rounded-4">
                                   <MyCard.Body classNames="p-6">
                                        <Step3ManageCandidate item={item} onPreviewCV={() => setPreviewItem(item)} onUpdate={fields => handleUpdateCandidateState(index, fields)} />
                                   </MyCard.Body>
                              </MyCard>
                         </div>
                    ))}
               </div>

               {/* Modal CV */}
               {previewItem && (
                    <MyModal
                         title={<span className="fw-boldest">Consultation : {previewItem.candidat.prenomNom}</span>}
                         show
                         onCloseCallback={() => setPreviewItem(null)}
                         noPadding
                         size="lg"
                    >
                         <iframe
                              src={FileAndDriveHelper.getUrlOfDriveFilePreview(previewItem.curriculum.googleDriveID as string)}
                              style={{ width: "100%", minHeight: "85vh", border: "none" }}
                              title="CV Preview"
                         />
                    </MyModal>
               )}
          </div>
     )
}

export default Step3ManagementView
